/**
 * plugins/table-row.ts — Tabelas comuns (tr/td em table, fora de .endpoint)
 *
 * Ferramentas: adicionar linha, deletar linha, mover ↑↓,
 *              toggle classe error-row, adicionar/remover coluna,
 *              toggle wrapper de scroll horizontal.
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, moveSibling, closestInside } from '../types';

export class TableRowPlugin implements VtbPlugin {
    readonly panelId = 'vtb-table-row';
    readonly selectorHints = ['table', 'tr', 'tbody tr'];

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        // Ignora tabelas dentro de .endpoint
        if (target.closest('.endpoint')) return null;

        const row = closestInside(target, visualEditor, e => 
            e.tagName === 'TR' || e.dataset.vtbId === 'tr'
        );
        if (!row) return null;
        
        const table = (row.closest('table') || row.closest('[data-vtb-id="table"]')) as HTMLElement | null;
        if (!table || table.closest('.endpoint')) return null;

        return { type: this.panelId, label: 'Linha de Tabela', el: row, container: table };
    }

    activate(ctx: SmartCtx, registry: IVtbRegistry): void {
        const row = ctx.el;
        const errBtn = registry.q<HTMLElement>('vtb-tr-error');
        if (errBtn) {
            errBtn.classList.toggle('vtb-btn-active', row.classList.contains('error-row'));
        }
        // Scroll wrapper
        const scrollBtn = registry.q<HTMLElement>('vtb-tr-scroll');
        const table = ctx.container as HTMLTableElement | null;
        if (scrollBtn && table) {
            const hasWrapper = table.parentElement?.classList.contains('table-scroll');
            scrollBtn.classList.toggle('vtb-btn-active', !!hasWrapper);
        }
    }

    setup(registry: IVtbRegistry): void {
        registry.q('vtb-tr-add')?.addEventListener('click',    () => this._addRow(registry));
        registry.q('vtb-tr-del')?.addEventListener('click',    () => this._delRow(registry));
        registry.q('vtb-tr-up')?.addEventListener('click',     () => this._move(registry, -1));
        registry.q('vtb-tr-down')?.addEventListener('click',   () => this._move(registry,  1));
        registry.q('vtb-tr-error')?.addEventListener('click',  () => this._toggleError(registry));
        registry.q('vtb-tr-col-add')?.addEventListener('click',() => this._addCol(registry));
        registry.q('vtb-tr-col-del')?.addEventListener('click',() => this._delCol(registry));
        registry.q('vtb-tr-scroll')?.addEventListener('click', () => this._toggleScroll(registry));
    }

    // ── Operações ─────────────────────────────────────────────────────────────

    private _addRow(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const row = ctx.el as HTMLTableRowElement;
        const colCount = row.cells.length;
        const newRow = row.cloneNode(false) as HTMLTableRowElement;
        for (let i = 0; i < colCount; i++) {
            const td = document.createElement('td');
            td.innerHTML = '&nbsp;';
            newRow.appendChild(td);
        }
        row.parentElement!.insertBefore(newRow, row.nextSibling);
        registry.sync();
        registry.selectEl(newRow);
    }

    private _delRow(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        ctx.el.remove();
        registry.clearSelection();
        registry.sync();
    }

    private _move(registry: IVtbRegistry, dir: -1 | 1): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        moveSibling(ctx.el, dir);
        registry.sync();
    }

    private _toggleError(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        ctx.el.classList.toggle('error-row');
        const btn = registry.q<HTMLElement>('vtb-tr-error');
        btn?.classList.toggle('vtb-btn-active', ctx.el.classList.contains('error-row'));
        registry.sync();
    }

    private _addCol(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || !ctx.container) return;
        ctx.container.querySelectorAll<HTMLTableRowElement>('tr').forEach(row => {
            const cell = document.createElement(row.querySelector('th') ? 'th' : 'td');
            cell.innerHTML = '&nbsp;';
            row.appendChild(cell);
        });
        registry.sync();
    }

    private _delCol(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || !ctx.container) return;
        const rows = Array.from(ctx.container.querySelectorAll<HTMLTableRowElement>('tr'));
        if (!rows[0] || rows[0].cells.length <= 1) return;
        rows.forEach(row => { row.deleteCell(row.cells.length - 1); });
        registry.sync();
    }

    private _toggleScroll(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || !ctx.container) return;
        const table = ctx.container;
        const parent = table.parentElement!;
        if (parent.classList.contains('table-scroll')) {
            parent.insertAdjacentElement('afterend', table);
            parent.remove();
            registry.q('vtb-tr-scroll')?.classList.remove('vtb-btn-active');
        } else {
            const wrapper = document.createElement('div');
            wrapper.className = 'table-scroll';
            parent.insertBefore(wrapper, table);
            wrapper.appendChild(table);
            registry.q('vtb-tr-scroll')?.classList.add('vtb-btn-active');
        }
        registry.sync();
    }
}
